/**
 * React Query hooks for magic link authentication API endpoints
 * Following CLAUDE.md guidelines: Always use React Query for REST API calls
 *
 * These hooks handle the password setup flow for first-time onboarding users
 * who received a magic link email instead of a temporary password.
 */
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { SERVER_BASE_URL } from "../utils/helpers";

// API base URL for onboarding auth endpoints
const ONBOARDING_API_BASE = SERVER_BASE_URL || "http://localhost:8080";

// Query keys for cache management
export const magicLinkKeys = {
  all: ["magic-link"] as const,
  setupPassword: () => [...magicLinkKeys.all, "setup-password"] as const,
  verifyToken: (token: string) =>
    [...magicLinkKeys.all, "verify-token", token] as const
};

/**
 * User info returned from successful password setup
 */
export interface SetupPasswordUser {
  email: string;
  roles: string[];
  uuid: string;
}

/**
 * Response type for the setup-password endpoint
 */
export interface SetupPasswordResponse {
  success: boolean;
  token: string; // Firebase custom token for immediate sign-in
  user: SetupPasswordUser;
}

/**
 * Request parameters for setting up password
 */
export interface SetupPasswordRequest {
  token: string; // Magic link JWT token
  newPassword: string; // The new password to set
}

/**
 * Response type for magic link token verification
 */
export interface VerifyTokenResponse {
  valid: boolean;
  email?: string;
  uuid?: string;
  message?: string;
  expired?: boolean;
  passwordAlreadySet?: boolean;
}

/**
 * Request parameters for verifying magic link token
 */
export interface VerifyTokenRequest {
  token: string;
}

/**
 * Hook to set up password using a magic link token.
 * This is used for first-time onboarding users who received a magic link email.
 *
 * @returns React Query mutation for setting up password
 *
 * @example
 * ```tsx
 * const setupPassword = useSetupPassword();
 *
 * const handleSubmit = async (newPassword: string) => {
 *   try {
 *     const result = await setupPassword.mutateAsync({
 *       token: magicLinkToken,
 *       newPassword
 *     });
 *     // Sign in with Firebase custom token
 *     await signInWithCustomToken(auth, result.token);
 *   } catch (error) {
 *     // Handle error (expired token, weak password, etc.)
 *   }
 * };
 * ```
 */
export function useSetupPassword() {
  return useMutation<SetupPasswordResponse, Error, SetupPasswordRequest>({
    mutationKey: magicLinkKeys.setupPassword(),
    mutationFn: async ({ token, newPassword }) => {
      const response = await axios.post<SetupPasswordResponse>(
        `${ONBOARDING_API_BASE}/onboarding-auth/setup-password`,
        { token, newPassword }
      );
      return response.data;
    }
  });
}

/**
 * Hook to verify a magic link token before showing the password setup form.
 * This allows pre-validation of the token to show appropriate error messages.
 *
 * @returns React Query mutation for token verification
 *
 * @example
 * ```tsx
 * const verifyToken = useVerifyMagicLinkToken();
 *
 * useEffect(() => {
 *   if (token) {
 *     verifyToken.mutate({ token });
 *   }
 * }, [token]);
 *
 * if (verifyToken.isError) {
 *   return <InvalidTokenMessage />;
 * }
 * ```
 */
export function useVerifyMagicLinkToken() {
  return useMutation<VerifyTokenResponse, Error, VerifyTokenRequest>({
    mutationKey: magicLinkKeys.verifyToken("verify"),
    mutationFn: async ({ token }) => {
      const response = await axios.post<VerifyTokenResponse>(
        `${ONBOARDING_API_BASE}/onboarding-auth/verify-magic-link`,
        { token }
      );
      return response.data;
    }
  });
}
